<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef } from '#imports';
import type { ImgMsgVO } from '@core/types/im';
import { watchElementVisible } from '../../composables/watch-element-visible';
import ImMsgBase from './ImMsgBase.vue';

interface Props {
  msg: ImgMsgVO;
}

const { msg } = defineProps<Props>();
const imgLoadError = ref(false);
const imgRef = useTemplateRef('img');

const emit = defineEmits(['retry', 'read', 'preview']);

function handleRetry() {
  emit('retry');
}
function handleRead() {
  emit('read');
}

const imgUrl = computed(() => {
  return msg.msgBody.imgMsg.url;
});
const maxWidth = 280;
const maxHeight = 168;
const renderHeight = ref(maxHeight);
calRenderHeight(msg.msgBody.imgMsg.width, msg.msgBody.imgMsg.height);
const imgStyle = computed(() => ({
  height: renderHeight.value + 'px',
}));

function handlePreview() {
  if (!imgLoadError.value) {
    emit('preview');
    handleRead();
  }
}
function handleImgError() {
  imgLoadError.value = true;
}
function handleImgLoad(e: Event) {
  imgLoadError.value = false;
  const target = e.target as HTMLImageElement | null;
  const ow = target?.naturalWidth;
  const oh = target?.naturalHeight;
  calRenderHeight(ow, oh);
}
function calRenderHeight(ow?: number, oh?: number) {
  if (ow && oh) {
    renderHeight.value = Math.min(maxHeight, (oh * maxWidth) / ow);
  } else {
    renderHeight.value = maxHeight;
  }
}

onMounted(() => {
  if (!msg.isMine && msg.readStatus === 0) {
    watchElementVisible(imgRef, handleRead);
  }
});
</script>

<template>
  <ImMsgBase
    :recall="msg.msgBody.recall"
    :avatar="msg.avatar"
    :show-datetime="msg.showDatetime"
    :time="msg.msgTime"
    :placement="msg.placement"
    :loading="msg.loading"
    :error="msg.error"
    @retry="handleRetry"
  >
    <div>
      <div
        v-if="imgLoadError"
        class="h-[168px] w-[280px] bg-colorFill flex-center"
      >
        <img
          class="h-14 w-14"
          src="@core/assets/image/im/icon-img-error.svg"
          alt="error"
        />
      </div>
      <img
        v-else
        ref="img"
        :src="imgUrl"
        alt="image"
        class="max-h-[168px] max-w-[280px] cursor-zoom-in object-contain"
        :style="imgStyle"
        @click="handlePreview"
        @error="handleImgError"
        @load="handleImgLoad"
      />
    </div>
  </ImMsgBase>
</template>

<style scoped></style>
